import React, { useEffect, useMemo } from 'react';

import classes from '@nerdwallet/base-styles/classes';
import classNames from 'classnames';

import { useLocationHash } from '~/app/lib/get-location-hash';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import sanitizeNumber from '~/app/lib/sanitize-number';
import { useMediaQuery } from '~/app/lib/use-media-query';
import BackgroundColor from '../background-color/background-color';
import { getEditingConfig } from './universal-container-editing-config';

import {
  universalContainerDefaultProps,
  universalContainerPropTypes,
} from './universal-container-utils';
import styles from './universal-container.module.scss';
import { stickyFooterHideStyle, useStickyFooter } from './use-sticky-footer';

function DefaultContent() {
  return <h1>Default Content</h1>;
}

/**
 * TODO: implement all params doc/types
 * @param {{style: React.CSSProperties}}
 * @returns
 */
const UniversalContainer = ({
  children,
  backgroundImageDesktop,
  backgroundImageTablet,
  backgroundImageMobile,
  backgroundColor: bgColor,
  hexColor,
  backgroundSize,
  padding,
  margin,
  border,
  width,
  alignment,
  hide,
  style,
  jumpLinkId,
  scrollOffsetDesktop,
  scrollOffsetTablet,
  scrollOffsetMobile,
  backgroundPositionX,
  backgroundPositionY,
  stickyFooter,
  ...rest
}) => {
  const backgroundImageSrcMap = useMemo(
    () => ({
      lg: backgroundImageDesktop,
      md: backgroundImageTablet,
      sm: backgroundImageMobile,
    }),
    [backgroundImageDesktop, backgroundImageMobile, backgroundImageTablet]
  );

  const {
    borderColor,
    borderWidth,
    shadowColor,
    borderIndividualColor,
    borderTopWidth,
    borderLeftWidth,
    borderRightWidth,
    borderBottomWidth,
    type: borderType,
    borderRounded,
  } = border;

  const borderColorStyle =
    borderType === 'color'
      ? {
          border: `${borderWidth || 0}px solid ${borderColor || '#cdcdcd'}`,
        }
      : null;

  const borderIndividualColorStyle =
    borderType === 'individual'
      ? {
          borderTop: `${borderTopWidth || 0}px solid ${
            borderIndividualColor || '#cdcdcd'
          }`,
          borderRight: `${borderRightWidth || 0}px solid ${
            borderIndividualColor || '#cdcdcd'
          }`,
          borderBottom: `${borderBottomWidth || 0}px solid ${
            borderIndividualColor || '#cdcdcd'
          }`,
          borderLeft: `${borderLeftWidth || 0}px solid ${
            borderIndividualColor || '#cdcdcd'
          }`,
        }
      : null;

  const borderShadowStyle =
    borderType === 'shadow' && shadowColor
      ? {
          boxShadow: `0px 1px 10px -1px ${shadowColor}`,
        }
      : null;

  const borderRadiusStyle =
    borderType !== 'none' &&
    (borderRounded?.type === 'custom' ||
      borderRounded?.type === 'individual') &&
    borderRounded?.value
      ? {
          borderRadius: borderRounded?.value,
        }
      : null;

  const size = useMediaQuery();
  const { editor } = useBuilder();

  const backgroundImage = useMemo(() => {
    if (backgroundImageSrcMap[size]) {
      return `url(${backgroundImageSrcMap[size]})`;
    }

    if (backgroundImageSrcMap.lg) {
      return `url(${backgroundImageSrcMap.lg})`;
    }

    if (backgroundImageSrcMap.lg) {
      return `url(${backgroundImageSrcMap.md})`;
    }

    return null;
  }, [backgroundImageSrcMap, size]);

  const bgOptions = { backgroundColor: bgColor, hexColor };

  const applyCurrencyRoundedBorders =
    borderType !== 'none' &&
    (borderRounded?.type === 'currency' || 'currency') &&
    (borderRounded?.currencyValue || 'border-radius-1');

  const paddingType = padding?.type || 'currency';
  const marginType = margin?.type || 'currency';
  const widthType = width?.type || 'currency';

  const getWidth = () => {
    const sizes = Object.entries(width?.screenSizes || {})?.reduce(
      (acc, [key, value]) => (value ? [...acc, key] : acc),
      []
    );

    if (!sizes.length && widthType) {
      return {
        width: width?.[widthType]?.value,
      };
    }

    return {
      ...(sizes?.length &&
        sizes?.includes(size) && {
          width: width?.[widthType]?.value,
        }),
    };
  };

  const getHideSize = () => {
    if (!hide || editor.showAllMediaViewPorts) {
      return {};
    }

    return hide[size] ? { display: 'none' } : {};
  };

  const scrollMargin = useMemo(
    () =>
      jumpLinkId &&
      ({
        lg: scrollOffsetDesktop,
        md: scrollOffsetTablet,
        sm: scrollOffsetMobile,
      }[size] ??
        scrollOffsetDesktop ??
        scrollOffsetTablet ??
        scrollOffsetMobile ??
        0),
    [
      size,
      jumpLinkId,
      scrollOffsetDesktop,
      scrollOffsetTablet,
      scrollOffsetMobile,
    ]
  );

  const getMargin = () => {
    if (margin?.type === 'custom') {
      return {
        marginTop:
          marginType === 'currency'
            ? Number(margin?.[marginType]?.top)
            : sanitizeNumber(Number(margin?.[marginType]?.top)),
        marginRight:
          marginType === 'currency'
            ? Number(margin?.[marginType]?.right)
            : sanitizeNumber(Number(margin?.[marginType]?.right)),
        marginBottom:
          marginType === 'currency'
            ? Number(margin?.[marginType]?.bottom)
            : sanitizeNumber(Number(margin?.[marginType]?.bottom)),
        marginLeft:
          marginType === 'currency'
            ? Number(margin?.[marginType]?.left)
            : sanitizeNumber(Number(margin?.[marginType]?.left)),
      };
    }
    return {
      marginTop:
        marginType === 'currency'
          ? margin?.[marginType]?.top
          : sanitizeNumber(margin?.[marginType]?.top),
      marginRight:
        marginType === 'currency'
          ? margin?.[marginType]?.right
          : sanitizeNumber(margin?.[marginType]?.right),
      marginBottom:
        marginType === 'currency'
          ? margin?.[marginType]?.bottom
          : sanitizeNumber(margin?.[marginType]?.bottom),
      marginLeft:
        marginType === 'currency'
          ? margin?.[marginType]?.left
          : sanitizeNumber(margin?.[marginType]?.left),
    };
  };

  const locationHash = useLocationHash();
  const jumpLinkElement =
    typeof window !== 'undefined'
      ? window?.document?.getElementById(jumpLinkId)
      : null;

  useEffect(() => {
    if (locationHash === jumpLinkId && jumpLinkElement) {
      setTimeout(() => {
        jumpLinkElement.scrollIntoView({ behavior: 'smooth' });
      }, 1000);
    }
  }, [jumpLinkElement, locationHash, jumpLinkId]);

  const { ref, stickyFooterMappedTargetId } = useStickyFooter(stickyFooter, {
    jumpLinkId,
  });

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <div
          ref={ref}
          id={jumpLinkId || stickyFooterMappedTargetId}
          className={classNames('container', [
            styles.container,
            {
              [classes(borderRounded?.currencyValue)]:
                applyCurrencyRoundedBorders,
              [classes('display-flex')]: alignment?.type === 'flex',
            },
          ])}
          style={{
            ...getHideSize(),
            ...getWidth(),
            ...(alignment?.direction &&
              alignment?.type === 'flex' && {
                flexDirection: alignment?.direction,
              }),
            ...(alignment?.horizontal &&
              alignment?.type === 'flex' && {
                alignItems: alignment?.horizontal,
              }),
            ...(alignment?.vertical &&
              alignment?.type === 'flex' && {
                justifyContent: alignment?.vertical,
              }),
            backgroundImage,
            backgroundColor,
            paddingTop:
              paddingType === 'currency'
                ? padding?.[paddingType]?.top
                : sanitizeNumber(padding?.[paddingType]?.top),
            paddingRight:
              paddingType === 'currency'
                ? padding?.[paddingType]?.right
                : sanitizeNumber(padding?.[paddingType]?.right),
            paddingBottom:
              paddingType === 'currency'
                ? padding?.[paddingType]?.bottom
                : sanitizeNumber(padding?.[paddingType]?.bottom),
            paddingLeft:
              paddingType === 'currency'
                ? padding?.[paddingType]?.left
                : sanitizeNumber(padding?.[paddingType]?.left),
            marginTop: getMargin(margin).marginTop,
            marginRight: getMargin(margin).marginRight,
            marginBottom: getMargin(margin).marginBottom,
            marginLeft: getMargin(margin).marginLeft,
            backgroundSize,
            backgroundPositionX,
            backgroundPositionY,
            scrollMarginTop: `${scrollMargin || 0}px`,

            ...borderColorStyle,
            ...borderIndividualColorStyle,
            ...borderShadowStyle,
            ...borderRadiusStyle,
            ...style,
            // Hide the sticky footer when the mapped target is hidden at the
            // initial state, otherwise it will be visible in the client side
            // and cause layout shift
            ...(stickyFooterMappedTargetId && stickyFooterHideStyle),
          }}
          {...rest}
        >
          {children || <DefaultContent />}
        </div>
      )}
    </BackgroundColor>
  );
};

UniversalContainer.displayName = 'Universal Container';
UniversalContainer.defaultProps = universalContainerDefaultProps;
UniversalContainer.propTypes = universalContainerPropTypes;
UniversalContainer.getEditingConfig = getEditingConfig;

export default UniversalContainer;
